import React, { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
  Typography,
  message,
} from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvService, {
  type RecordFuelPayload,
  type RecordInspectionPayload,
  type ReportBreakdownPayload,
} from '@/services/pmv.service';
import employeeService from '@/services/employee.service';
import { formatEmployeeOption } from '@/utils/pmvDisplay';

const { Title } = Typography;

function useProfileHeader(profileId: string | undefined) {
  const wsQ = useQuery({
    queryKey: ['pmv-workspace', profileId],
    queryFn: () => pmvService.getWorkspace(profileId!),
    enabled: Boolean(profileId),
  });
  const ws = wsQ.data as Record<string, any> | undefined;
  const asset = ws?.asset;
  const classification = ws?.classification;
  const allocation =
    ws?.operational?.allocation?.state === 'ACTIVE'
      ? {
          id: String(ws.operational.allocation.allocation_id),
          project_id: String(ws.operational.allocation.project_id),
        }
      : null;
  return {
    loading: wsQ.isLoading,
    documentLabel: classification?.name ?? 'PMV Unit',
    documentNumber: ws?.profile?.fleet_number || asset?.asset_code || '',
    classFamily: classification?.class_family as string | undefined,
    allocation,
    specifications: (ws?.specifications ?? []) as Array<{
      definition: {
        id: string;
        code: string;
        label: string;
        data_type: string;
        unit_of_measure?: string;
      };
      value: { value_text?: string; value_number?: number } | null;
    }>,
  };
}

export const PmvRecordInspectionFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const templateId = search.get('template_id');
  const navigate = useNavigate();
  const header = useProfileHeader(id);
  const [itemResults, setItemResults] = useState<Record<string, 'PASS' | 'FAIL' | 'DEFECT' | 'NA'>>({});
  const [remarks, setRemarks] = useState('');

  const templatesQ = useQuery({
    queryKey: ['pmv-inspection-templates', id],
    queryFn: () => pmvService.listProfileInspectionTemplates(id!),
    enabled: Boolean(id),
  });

  const selectedTemplate = useMemo(
    () => (templatesQ.data ?? []).find((t) => t.id === templateId) ?? null,
    [templatesQ.data, templateId]
  );

  React.useEffect(() => {
    if (!selectedTemplate) return;
    const defaults: Record<string, 'PASS' | 'FAIL' | 'DEFECT' | 'NA'> = {};
    for (const item of selectedTemplate.checklist_items_json ?? []) {
      const code = item.code ?? item.label ?? 'item';
      defaults[code] = 'PASS';
    }
    setItemResults(defaults);
  }, [selectedTemplate?.id]);

  const mut = useMutation({
    mutationFn: (body: RecordInspectionPayload) => pmvService.recordProfileInspection(id!, body),
    onSuccess: (data) => {
      message.success(`Inspection recorded — ${data.overall}`);
      navigate(`/asset/pmv/${id}`);
    },
    onError: (e: Error) => message.error(e.message || 'Inspection failed'),
  });

  const submit = () => {
    if (!selectedTemplate) return;
    const items = (selectedTemplate.checklist_items_json ?? []).map((item, idx) => {
      const code = item.code ?? `item_${idx + 1}`;
      return {
        item_code: code,
        item_label: item.label ?? code,
        item_result: itemResults[code] ?? 'PASS',
        is_mandatory: item.mandatory !== false,
      };
    });
    mut.mutate({
      template_id: selectedTemplate.id,
      items,
      allocation_id: header.allocation?.id ?? null,
      project_id: header.allocation?.project_id ?? null,
      remarks: remarks || null,
    });
  };

  if (header.loading || templatesQ.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Record inspection"
          documentNumber={header.documentNumber}
        />
      }
    >
      <Title level={5}>{selectedTemplate?.template_name ?? 'Select a template from PMV 360'}</Title>
      {!selectedTemplate ? (
        <Alert type="warning" showIcon message="Missing template. Open PMV 360 and choose an inspection template." />
      ) : (
        <Form layout="vertical" style={{ maxWidth: 640 }}>
          {(selectedTemplate.checklist_items_json ?? []).map((item, idx) => {
            const code = item.code ?? `item_${idx + 1}`;
            return (
              <Form.Item key={code} label={item.label ?? code}>
                <Select
                  value={itemResults[code] ?? 'PASS'}
                  onChange={(v) => setItemResults((prev) => ({ ...prev, [code]: v }))}
                  options={[
                    { value: 'PASS', label: 'Pass' },
                    { value: 'DEFECT', label: 'Defect (non-blocking)' },
                    { value: 'FAIL', label: 'Fail' },
                    { value: 'NA', label: 'N/A' },
                  ]}
                />
              </Form.Item>
            );
          })}
          <Form.Item label="Remarks">
            <Input.TextArea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </Form.Item>
          <Space>
            <Button type="primary" loading={mut.isPending} onClick={submit}>
              Record
            </Button>
            <Button onClick={() => navigate(`/asset/pmv/${id}`)}>Cancel</Button>
          </Space>
        </Form>
      )}
    </WorkspaceLayout>
  );
};

export const PmvReportBreakdownFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const header = useProfileHeader(id);
  const [form] = Form.useForm();

  const mut = useMutation({
    mutationFn: (body: ReportBreakdownPayload) => pmvService.reportProfileBreakdown(id!, body),
    onSuccess: () => {
      message.success('Breakdown reported');
      navigate(`/asset/pmv/${id}`);
    },
    onError: (e: Error) => message.error(e.message || 'Report failed'),
  });

  return (
    <WorkspaceLayout
      header={<EnterprisePageHeader documentLabel="Report breakdown" documentNumber={header.documentNumber} />}
    >
      <Title level={5}>Breakdown event</Title>
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 640 }}
        initialValues={{ severity: 'MEDIUM', escalate_to_maintenance: false }}
        onFinish={(v) =>
          mut.mutate({
            title: v.title,
            description: v.description || null,
            severity: v.severity,
            allocation_id: header.allocation?.id ?? null,
            project_id: header.allocation?.project_id ?? null,
            escalate_to_maintenance: Boolean(v.escalate_to_maintenance),
          })
        }
      >
        <Form.Item name="title" label="Title" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item name="severity" label="Severity">
          <Select
            options={[
              { value: 'LOW', label: 'Low' },
              { value: 'MEDIUM', label: 'Medium' },
              { value: 'HIGH', label: 'High' },
              { value: 'CRITICAL', label: 'Critical' },
            ]}
          />
        </Form.Item>
        <Form.Item name="escalate_to_maintenance" valuePropName="checked">
          <Checkbox>Create maintenance case immediately</Checkbox>
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={mut.isPending}>
            Report
          </Button>
          <Button onClick={() => navigate(`/asset/pmv/${id}`)}>Cancel</Button>
        </Space>
      </Form>
    </WorkspaceLayout>
  );
};

export const PmvRecordFuelFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const header = useProfileHeader(id);
  const [form] = Form.useForm();
  const isVehicle = String(header.classFamily ?? '').toUpperCase() === 'VEHICLE';

  const mut = useMutation({
    mutationFn: (body: RecordFuelPayload) => pmvService.recordProfileFuelAttribution(id!, body),
    onSuccess: () => {
      message.success('Fuel attribution recorded');
      navigate(`/asset/pmv/${id}`);
    },
    onError: (e: Error) => message.error(e.message || 'Record failed'),
  });

  return (
    <WorkspaceLayout
      header={<EnterprisePageHeader documentLabel="Record fuel fill" documentNumber={header.documentNumber} />}
    >
      <Title level={5}>Fuel attribution</Title>
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 640 }}
        initialValues={{ fuel_type: 'DIESEL' }}
        onFinish={(v) =>
          mut.mutate({
            fuel_type: v.fuel_type,
            quantity_liters: v.quantity_liters,
            hour_meter_reading: v.hour_meter_reading ?? null,
            odometer_reading: v.odometer_reading ?? null,
            allocation_id: header.allocation?.id ?? null,
            project_id: header.allocation?.project_id ?? null,
            remarks: v.remarks || null,
          })
        }
      >
        <Form.Item name="fuel_type" label="Fuel type">
          <Select
            options={[
              { value: 'DIESEL', label: 'Diesel' },
              { value: 'PETROL', label: 'Petrol' },
              { value: 'OTHER', label: 'Other' },
            ]}
          />
        </Form.Item>
        <Form.Item name="quantity_liters" label="Quantity (liters)" rules={[{ required: true }]}>
          <InputNumber min={0.1} step={0.1} style={{ width: '100%' }} />
        </Form.Item>
        {!isVehicle && (
          <Form.Item name="hour_meter_reading" label="Hour meter at fill">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        )}
        {isVehicle && (
          <Form.Item name="odometer_reading" label="Odometer at fill">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        )}
        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={mut.isPending}>
            Record
          </Button>
          <Button onClick={() => navigate(`/asset/pmv/${id}`)}>Cancel</Button>
        </Space>
      </Form>
    </WorkspaceLayout>
  );
};

export const PmvAssignOperatorFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const header = useProfileHeader(id);
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [role, setRole] = useState<'OPERATOR' | 'DRIVER' | 'SPOTTER'>('OPERATOR');
  const [remarks, setRemarks] = useState('');

  const employeesQ = useQuery({
    queryKey: ['pmv-operator-employees'],
    queryFn: () => employeeService.list({ employment_status: 'Active' }),
  });
  const eligibilityQ = useQuery({
    queryKey: ['pmv-operator-eligibility', id, employeeId],
    queryFn: () => pmvService.checkOperatorEligibility(id!, employeeId!),
    enabled: Boolean(id && employeeId),
  });

  const mut = useMutation({
    mutationFn: () =>
      pmvService.assignProfileOperator(id!, {
        employee_id: employeeId!,
        assignment_role: role,
        allocation_id: header.allocation?.id ?? null,
        project_id: header.allocation?.project_id ?? null,
        remarks: remarks || null,
      }),
    onSuccess: () => {
      message.success('Operator assigned');
      navigate(`/asset/pmv/${id}`);
    },
    onError: (e: Error) => message.error(e.message || 'Assign failed'),
  });

  return (
    <WorkspaceLayout
      header={<EnterprisePageHeader documentLabel="Assign operator" documentNumber={header.documentNumber} />}
    >
      <Title level={5}>Operator assignment</Title>
      <Form layout="vertical" style={{ maxWidth: 640 }}>
        <Form.Item label="Employee" required>
          <Select
            showSearch
            placeholder="Select employee"
            value={employeeId}
            onChange={setEmployeeId}
            optionFilterProp="label"
            options={(employeesQ.data ?? []).map((e) => ({
              value: e.id,
              label: formatEmployeeOption(e),
            }))}
          />
        </Form.Item>
        <Form.Item label="Role">
          <Select
            value={role}
            onChange={setRole}
            options={[
              { value: 'OPERATOR', label: 'Operator' },
              { value: 'DRIVER', label: 'Driver' },
              { value: 'SPOTTER', label: 'Spotter' },
            ]}
          />
        </Form.Item>
        <Form.Item label="Remarks">
          <Input.TextArea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        </Form.Item>
        {eligibilityQ.data && (
          <Alert
            style={{ marginBottom: 16 }}
            type={eligibilityQ.data.eligible ? 'success' : 'error'}
            message={eligibilityQ.data.eligible ? 'Eligible' : 'Not eligible'}
            description={
              eligibilityQ.data.blockers.length > 0
                ? eligibilityQ.data.blockers.join('; ')
                : eligibilityQ.data.checks.map((c) => c.message).filter(Boolean).join('; ')
            }
          />
        )}
        <Space>
          <Button
            type="primary"
            loading={mut.isPending}
            disabled={!employeeId || eligibilityQ.data?.eligible === false}
            onClick={() => mut.mutate()}
          >
            Assign
          </Button>
          <Button onClick={() => navigate(`/asset/pmv/${id}`)}>Cancel</Button>
        </Space>
      </Form>
    </WorkspaceLayout>
  );
};

export const PmvChangeStatusFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const header = useProfileHeader(id);
  const [form] = Form.useForm();

  const mut = useMutation({
    mutationFn: (body: { to_status: string; reason_text?: string }) => pmvService.statusTransition(id!, body),
    onSuccess: () => {
      message.success('Status updated');
      navigate(`/asset/pmv/${id}`);
    },
    onError: (e: Error) => message.error(e.message || 'Status change failed'),
  });

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader documentLabel="Change operational status" documentNumber={header.documentNumber} />
      }
    >
      <Title level={5}>Status transition</Title>
      <Form form={form} layout="vertical" style={{ maxWidth: 640 }} onFinish={(v) => mut.mutate(v)}>
        <Form.Item name="to_status" label="To status" rules={[{ required: true }]}>
          <Select
            options={[
              { value: 'AVAILABLE', label: 'Available' },
              { value: 'OUT_OF_SERVICE', label: 'Out of service' },
              { value: 'RESERVED', label: 'Reserved' },
              { value: 'UNDER_INSPECTION', label: 'Under inspection' },
            ]}
          />
        </Form.Item>
        <Form.Item name="reason_text" label="Reason">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={mut.isPending}>
            Save
          </Button>
          <Button onClick={() => navigate(`/asset/pmv/${id}`)}>Cancel</Button>
        </Space>
      </Form>
    </WorkspaceLayout>
  );
};

export const PmvEditSpecificationsFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const header = useProfileHeader(id);
  const [form] = Form.useForm();

  React.useEffect(() => {
    if (!header.specifications.length) return;
    form.setFieldsValue(
      Object.fromEntries(
        header.specifications.map((s) => [
          s.definition.id,
          s.value?.value_number ?? s.value?.value_text ?? undefined,
        ])
      )
    );
  }, [header.specifications, form]);

  const mut = useMutation({
    mutationFn: (values: Array<{ attribute_definition_id: string; value_text?: string; value_number?: number }>) =>
      pmvService.putSpecifications(id!, values),
    onSuccess: () => {
      message.success('Specifications saved');
      navigate(`/asset/pmv/${id}`);
    },
    onError: (e: Error) => message.error(e.message || 'Save failed'),
  });

  if (header.loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <WorkspaceLayout
      header={<EnterprisePageHeader documentLabel="Edit specifications" documentNumber={header.documentNumber} />}
    >
      <Title level={5}>Unit specifications</Title>
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 640 }}
        onFinish={() => {
          const vals = header.specifications.map((s) => {
            const key = s.definition.id;
            const raw = form.getFieldValue(key);
            if (
              s.definition.data_type === 'DECIMAL' ||
              s.definition.data_type === 'NUMBER' ||
              s.definition.data_type === 'INTEGER'
            ) {
              return {
                attribute_definition_id: key,
                value_number: raw != null ? Number(raw) : undefined,
              };
            }
            return {
              attribute_definition_id: key,
              value_text: raw != null ? String(raw) : undefined,
            };
          });
          mut.mutate(vals);
        }}
      >
        {header.specifications.map((s) => (
          <Form.Item
            key={s.definition.id}
            name={s.definition.id}
            label={`${s.definition.label}${s.definition.unit_of_measure ? ` (${s.definition.unit_of_measure})` : ''}`}
          >
            {s.definition.data_type === 'DECIMAL' ||
            s.definition.data_type === 'NUMBER' ||
            s.definition.data_type === 'INTEGER' ? (
              <InputNumber style={{ width: '100%' }} />
            ) : (
              <Input />
            )}
          </Form.Item>
        ))}
        <Space>
          <Button type="primary" htmlType="submit" loading={mut.isPending}>
            Save
          </Button>
          <Button onClick={() => navigate(`/asset/pmv/${id}`)}>Cancel</Button>
        </Space>
      </Form>
    </WorkspaceLayout>
  );
};